<script>
import Header from '@components/navigation/Header';
import Footer from '@components/navigation/Footer';
import SidebarNav from '@components/navigation/SidebarNav';
import MainNavigation from '@components/navigation/MainNavigation';
import ApiProgress from '@src/plugins/ApiProgress';
import { PtAlert } from '@pathao/ui-components/esm/Alert';
import { PtButton } from '@pathao/ui-components/esm/Button';
import { mapGetters } from 'vuex';
import { requestNotificationPermission } from '../../firebase';

export default {
  name: 'MainLayout',
  components: {
    Header,
    SidebarNav,
    MainNavigation,
    Footer,
    PtAlert,
    PtButton,
  },
  mixins: [ ApiProgress ],
  props: {
    customClass: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      infoBarExpanded: false,
      showAlert: false,
      notificationStatus: 'default',
      checkInterval: null,
    };
  },
  computed: {
    pageStyle() {
      return {
        // marginLeft: '50px',
        marginRight: this.infoBarExpanded ? '250px' : '0px',
        transition: 'all 0.3s',
      };
    },
    notificationMessage() {
      const messages = {
        denied: 'Notifications are currently blocked. Click the button to request permission again.',
        default: 'Please allow notifications to receive updates.',
      };
      return messages[this.notificationStatus] || '';
    },
    ...mapGetters({
      user: 'auth/user',
    }),
  },
  mounted() {
    if (window.zE) {
      window.zE('webWidget', 'show');
    }

    // Check notification status for non-admin users
    if (this.user && this.user.name && (this.user.type !== 'admin')) {
      this.checkNotificationStatus();
      // Start polling every 60 seconds
      this.checkInterval = setInterval(this.checkNotificationStatus, 60000);
    }
  },
  beforeDestroy() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }
  },
  methods: {
    stopNotificationSound() {
      if (window.placedSound && window.placedSound.playing()) {
        window.placedSound.stop();
        delete window.placedSound;
      }
    },
    closeAlert() {
      this.showAlert = false;
    },
    async requestPermission() {
      console.log('[main.vue] Requesting notification permission...');
      await requestNotificationPermission();
      console.log('[main.vue] Permission request completed');
      this.checkNotificationStatus();
      console.log('[main.vue] Current permission status:', Notification.permission);
      // Register FCM token if permission was granted
      if (Notification.permission === 'granted') {
        console.log('[main.vue] Permission granted, registering FCM token...');
        try {
          await this.$store.dispatch('auth/registerFCMToken');
          console.log('[main.vue] FCM token registration completed successfully');
        } catch (err) {
          console.error('[main.vue] Failed to register FCM token:', err);
        }
      } else {
        console.log('[main.vue] Permission not granted, skipping FCM registration');
      }
    },
    async checkNotificationStatus() {
      console.log('[main.vue] Checking notification status...');
      const Notification = window.Notification || window.mozNotification || window.webkitNotification;
      if (!Notification) {
        console.warn('[main.vue] Notifications not supported in this browser');
        this.notificationStatus = 'denied';
        this.showAlert = true;
        return;
      }

      const permission = await Notification.permission;
      console.log('[main.vue] Current permission status:', permission);
      this.notificationStatus = permission;
      this.showAlert = permission !== 'granted';

      // If permission is granted, clear the interval
      if (permission === 'granted' && this.checkInterval) {
        clearInterval(this.checkInterval);
        this.checkInterval = null;
      }
    },
  },
};
</script>

<template>
  <div
    class="min-h-screen bg-grey-lighter flex flex-1 flex-col"
    @click="stopNotificationSound"
  >
    <div :style="pageStyle" class="flex flex-col flex-1">
      <Header />
      <MainNavigation />
      <div :class="customClass" class="px-8 py-4">
        <pt-alert
          v-if="showAlert"
          :variant="notificationStatus === 'denied' ? 'error' : 'warning'"
          class="custom-alert"
          dismissible
          @close="closeAlert"
        >
          <div class="flex items-center justify-between">
            <span>{{ notificationMessage }}</span>
            <pt-button
              v-if="notificationStatus !== 'denied'"
              variant="primary"
              class="ml-4"
              @action="requestPermission"
            >
              Allow
            </pt-button>
            <button
              v-if="notificationStatus === 'denied'"
              class="primary primary-button bg-green rounded p-2 text-white ml-4 mt-2 "
              @click="requestPermission"
            >
              Ask Permission
          </button>
          </div>
        </pt-alert>
        <slot />
      </div>
    </div>

    <sidebar-nav />

  </div>
</template>

<style lang="scss">
.custom-alert {
  margin: 0 0 1rem 0;
}
</style>
